import { baseApi } from '@/services/api/baseApi';
import { registerMockHandler } from '@/services/api/mockBaseQuery';

import { mockUserFixture } from './auth.fixtures';
import type { LoginRequest, LoginResponse, SessionResponse } from '../types/auth.types';

/**
 * Mock-mode session handlers (`VITE_USE_MOCK_API=true`).
 *
 * `getSession` always succeeds as the fixture user — there is no real
 * backend to probe a cookie against yet, so mock mode treats every visit
 * as already signed in. `login`/`logout` accept any input; only their
 * shape needs to match the real contract.
 */
registerMockHandler('GET', '/auth/session', () => ({ user: mockUserFixture }));
registerMockHandler('POST', '/auth/login', () => ({ user: mockUserFixture }));
registerMockHandler('POST', '/auth/logout', () => undefined);

/**
 * Authentication endpoints, injected into the root API slice.
 *
 * Injection (rather than a separate `createApi`) keeps one cache, one
 * middleware, and one set of tags for the whole application.
 */
export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Probes the existing session cookie on application start.
     *
     * A 401 here is the expected "not signed in" answer, not an error worth
     * surfacing to the user.
     */
    getSession: builder.query<SessionResponse, void>({
      query: () => ({ url: '/auth/session', method: 'GET' }),
      providesTags: ['Auth'],
    }),

    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (credentials) => ({
        url: '/auth/login',
        method: 'POST',
        body: credentials,
      }),
      // A new session invalidates every cached response, since visibility of
      // data depends on who is signed in.
      invalidatesTags: ['Auth', 'User', 'AgencyOverview', 'Account', 'Invoice'],
    }),

    logout: builder.mutation<void, void>({
      query: () => ({ url: '/auth/logout', method: 'POST' }),
      invalidatesTags: ['Auth', 'User', 'AgencyOverview', 'Account', 'Invoice'],
    }),
  }),
  // Endpoints are injected once; overriding signals an accidental duplicate.
  overrideExisting: false,
});

export const { useGetSessionQuery, useLoginMutation, useLogoutMutation } = authApi;
